import { FC, useEffect, useCallback } from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { Button, LinkButton, Modal } from 'src/components';
import { ROUTES } from 'src/constants/routes.const';
import { addToSoftSellStorageEntry } from 'src/apps/Monetisation/SoftSellScreen/softSellStorageEntry';
import { useUserContext } from 'src/context/user';
import { TrackingApi } from 'src/api';
import { SoftSellTrackingAction, SoftSellTrackingType } from 'src/api/tracking/types';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import conversationsImageSrc from './images/conversations.png';
import wordlistsImageSrc from './images/wordlists.png';
import videosImageSrc from './images/videos.png';
import reviewImageSrc from './images/review.png';
import buddiesImageSrc from './images/buddies.png';
import * as S from './styles';

export type SoftSellScreenType = 'wordlists' | 'review' | 'videos' | 'conversations' | 'buddies';

type ContentMessages = {
  title: MessageDescriptor;
  description: MessageDescriptor;
  cta: MessageDescriptor;
};

const msgsByType: Record<SoftSellScreenType, ContentMessages> = {
  wordlists: defineMessages({
    title: { id: 'monetisation_softsell_wordlist_title' },
    description: { id: 'monetisation_softsell_wordlist_text' },
    cta: { id: 'monetisation_softsell_wordlist_cta' },
  }),
  review: defineMessages({
    title: { id: 'monetisation_softsell_review_title' },
    description: { id: 'monetisation_softsell_review_text' },
    cta: { id: 'monetisation_softsell_review_cta' },
  }),
  videos: defineMessages({
    title: { id: 'monetisation_softsell_video_title' },
    description: { id: 'monetisation_softsell_video_text' },
    cta: { id: 'monetisation_softsell_video_cta' },
  }),
  conversations: defineMessages({
    title: { id: 'monetisation_softsell_buddies_conversation_title' },
    description: { id: 'monetisation_softsell_conversation_text' },
    cta: { id: 'monetisation_softsell_conversation_cta' },
  }),
  buddies: defineMessages({
    title: { id: 'monetisation_softsell_buddies_conversation_title' },
    description: { id: 'monetisation_softsell_buddies_text' },
    cta: { id: 'monetisation_softsell_buddies_cta' },
  }),
};

const imgsByType: Record<SoftSellScreenType, string> = {
  wordlists: wordlistsImageSrc,
  review: reviewImageSrc,
  videos: videosImageSrc,
  conversations: conversationsImageSrc,
  buddies: buddiesImageSrc,
};

const ctasByType: Record<SoftSellScreenType, string> = {
  wordlists: ROUTES.wordlists.main,
  review: ROUTES.aprender.review,
  videos: ROUTES.video.immerse,
  conversations: ROUTES.membot.select,
  buddies: ROUTES.ai.buddies,
};

const msgs = defineMessages({
  continue: { id: 'monetisation_softsell_wordlist_skip' },
});

export interface SoftSellScreenProps {
  screenType: SoftSellScreenType;
  trackingType: SoftSellTrackingType;
  onContinue: () => void;
}

export const SoftSellScreen: FC<SoftSellScreenProps> = ({
  screenType,
  trackingType,
  onContinue,
}) => {
  const { user } = useUserContext();
  const intl = useIntl();
  const languagePairId = getLanguagePairId();

  const contentMsgs = msgsByType[screenType];
  const imageSrc = imgsByType[screenType];
  const ctaHref = ctasByType[screenType];

  const trackAction = useCallback(
    (action: SoftSellTrackingAction) => {
      void TrackingApi.trackUserAction(action, 'soft_sell_screen', languagePairId, !!user?.isPro, {
        type: trackingType,
      });
    },
    [trackingType, languagePairId, user?.isPro],
  );

  /* istanbul ignore next: please test in WEBSITE-7219 */
  useEffect(() => {
    addToSoftSellStorageEntry(user, screenType);
    trackAction('screen_viewed');
  }, [user, screenType, trackAction]);

  const handleBrowseClick = useCallback(() => {
    trackAction('clicked_soft_sell_browse_button');
  }, [trackAction]);

  // The soft-sell modal is dismissed without a purchase via the "continue" CTA or the
  // modal close. Emit the canonical Phase 2 exit event alongside the existing continue click.
  const handleContinueClick = useCallback(() => {
    trackAction('clicked_soft_sell_continue_button');
    trackAction('paywall_dismissed');
    onContinue();
  }, [trackAction, onContinue]);

  return (
    <Modal isOpen fullSize hideCloseButton handleClose={onContinue}>
      <S.ContentOuterContainer>
        <S.ContentInnerContainer>
          <S.Image src={imageSrc} alt="" />
          <S.Title>{intl.formatMessage(contentMsgs.title)}</S.Title>
          <S.Description>{intl.formatMessage(contentMsgs.description)}</S.Description>
          <S.ButtonContainer>
            <LinkButton
              buttonType="primary"
              buttonSize="large"
              wide
              href={ctaHref}
              onClick={handleBrowseClick}
            >
              {intl.formatMessage(contentMsgs.cta)}
            </LinkButton>
            <Button buttonType="secondary" buttonSize="large" wide onClick={handleContinueClick}>
              {intl.formatMessage(msgs.continue)}
            </Button>
          </S.ButtonContainer>
        </S.ContentInnerContainer>
      </S.ContentOuterContainer>
    </Modal>
  );
};
